/**
 * cdn-domain-rate-limiting-tab.tsx — Rate Limiting tab for a CDN domain (act-as).
 *
 * Rule list: match path + threshold + window + action. Full PUT replaces rules array.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/rate-limiting
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Gauge } from 'lucide-react';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, Skeleton, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Dialog, DialogHeader, DialogBody, DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNRateLimiting,
  actAsCDNSecurityKeys,
  type RateLimitRule,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

const ACTIONS = ['block', 'challenge', 'log'] as const;
const ACTION_TONE: Record<string, 'danger' | 'warning' | 'info'> = {
  block: 'danger', challenge: 'warning', log: 'info',
};

function blankRule(): RateLimitRule {
  return { name: '', enabled: true, match_path: '/*', threshold: 100, window_secs: 60, action: 'block', response_code: 429 };
}

interface Props { domainId: string }

export function CdnDomainRateLimitingTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNRateLimiting(domainId);
  const [rules, setRules] = useState<RateLimitRule[]>([]);
  const [snapshot, setSnapshot] = useState<RateLimitRule[]>([]);
  const [editing, setEditing] = useState<RateLimitRule | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data?.rules ?? [];
    setRules(next);
    setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(rules) !== JSON.stringify(snapshot);

  const openNew = () => { setEditing(blankRule()); setEditIdx(null); };
  const openEdit = (r: RateLimitRule, i: number) => { setEditing({ ...r }); setEditIdx(i); };
  const saveEdit = () => {
    if (!editing) return;
    setRules((prev) => editIdx === null ? [...prev, editing] : prev.map((r, i) => i === editIdx ? editing : r));
    setEditing(null); setEditIdx(null);
  };
  const deleteRule = (i: number) => setRules((prev) => prev.filter((_, idx) => idx !== i));

  if (q.isLoading) return <Skeleton style={{ height: 280 }} />;
  if (q.isError) {
    return <TabError title={t('cdn_domain.rate_limit_tab.load_error', 'Failed to load rate limiting rules')} onRetry={() => q.refetch()} />;
  }

  return (
    <Section
      title={t('cdn_domain.rate_limit_tab.title', 'Rate Limiting')}
      description={t('cdn_domain.rate_limit_tab.description', 'Threshold-based rate limiting rules for this domain.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader
            actions={
              <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={openNew}>
                {t('cdn_domain.rate_limit_tab.add_rule', 'Add rule')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.rate_limit_tab.section_rules', 'Rules')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.rate_limit_tab.section_rules_desc', '{{n}} rule(s) active.', { n: rules.filter((r) => r.enabled).length })}
            </CardDescription>
          </CardHeader>
          <CardBody>
            {rules.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Gauge size={20} />}
                title={t('cdn_domain.rate_limit_tab.no_rules', 'No rate limiting rules')}
                description={t('cdn_domain.rate_limit_tab.no_rules_desc', 'Add a rule to throttle excessive requests.')}
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {rules.map((rule, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)',
                    padding: 'var(--vn-space-3)', borderRadius: 'var(--vn-radius-md)',
                    background: 'var(--vn-bg-subtle)', opacity: rule.enabled ? 1 : 0.55,
                  }}>
                    <span style={{ flex: 1, fontWeight: 500, fontSize: 'var(--vn-font-size-sm)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rule.name || rule.match_path || t('cdn_domain.rate_limit_tab.untitled_rule', 'Untitled')}
                    </span>
                    <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                      {rule.threshold} req / {rule.window_secs}s
                    </span>
                    <Badge tone={ACTION_TONE[rule.action] ?? 'neutral'} appearance="subtle" size="sm">
                      {rule.action}
                    </Badge>
                    <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => openEdit(rule, i)}><Pencil size={14} /></IconButton>
                    <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => deleteRule(i)}><Trash2 size={14} /></IconButton>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setRules(snapshot)}>{t('common.cancel', 'Cancel')}</Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>{t('cdn_domain.rate_limit_tab.action_save', 'Save')}</Button>
            </>
          }
        />
      </div>

      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) { setEditing(null); setEditIdx(null); } }}>
          <DialogHeader>
            {editIdx === null ? t('cdn_domain.rate_limit_tab.dialog_add_title', 'Add rate limit rule') : t('cdn_domain.rate_limit_tab.dialog_edit_title', 'Edit rate limit rule')}
          </DialogHeader>
          <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--vn-space-3)' }}>
              <Field label={t('cdn_domain.rate_limit_tab.field_name', 'Name')}>
                {(p) => <Input {...p} value={editing.name} placeholder="Block API abuse"
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />}
              </Field>
              <Field label={t('cdn_domain.rate_limit_tab.field_enabled', 'Enabled')}>
                {() => <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />}
              </Field>
            </div>
            <Field label={t('cdn_domain.rate_limit_tab.field_path', 'Match path')} description={t('cdn_domain.rate_limit_tab.field_path_desc', 'Glob pattern, e.g. /api/*')}>
              {(p) => <Input {...p} value={editing.match_path ?? ''} placeholder="/*"
                onChange={(e) => setEditing({ ...editing, match_path: e.target.value })} />}
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label={t('cdn_domain.rate_limit_tab.field_threshold', 'Threshold (requests)')}>
                {(p) => <NumericInput {...p} value={editing.threshold}
                  onChange={(e) => setEditing({ ...editing, threshold: Number(e.target.value) })} />}
              </Field>
              <Field label={t('cdn_domain.rate_limit_tab.field_window', 'Window (seconds)')}>
                {(p) => <NumericInput {...p} value={editing.window_secs}
                  onChange={(e) => setEditing({ ...editing, window_secs: Number(e.target.value) })} />}
              </Field>
              <Field label={t('cdn_domain.rate_limit_tab.field_response_code', 'Response code')}>
                {(p) => <NumericInput {...p} value={editing.response_code ?? 429}
                  onChange={(e) => setEditing({ ...editing, response_code: Number(e.target.value) })} />}
              </Field>
            </div>
            <Field label={t('cdn_domain.rate_limit_tab.field_action', 'Action')}>
              {(p) => (
                <Select value={editing.action} onValueChange={(v) => setEditing({ ...editing, action: v as RateLimitRule['action'] })}>
                  <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setEditing(null); setEditIdx(null); }}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={saveEdit}>
              {editIdx === null ? t('cdn_domain.rate_limit_tab.dialog_add_btn', 'Add') : t('cdn_domain.rate_limit_tab.dialog_save_btn', 'Save')}
            </Button>
          </DialogFooter>
        </Dialog>
      )}

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.rate_limit_tab.confirm_save_title', 'Save rate limiting rules')}
        summary={t('cdn_domain.rate_limit_tab.save_summary', 'Update {{n}} rate limit rule(s) for this domain.', { n: rules.length })}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.rateLimiting(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/rate-limiting`, { rules })}
        onSuccess={() => setSnapshot(rules)}
      />
    </Section>
  );
}
